import { Divider, Grid } from '@mantine/core';
import type { UseQueryResult } from 'react-query';
import { Detail } from '../../../components/Detail';
import { InfoBadge } from '../../../components/InfoBadge';
import {
	formatToCurrencyOrDefault,
	formatToDateString,
} from '../../../util/formatting';
import type { ApiResponse } from '../../../util/types/Response';
import type { FullCustomerDto } from '../types/CustomerDto';

interface CustomerTileProps {
	customerQuery: UseQueryResult<ApiResponse<FullCustomerDto>, unknown>;
}

export function CustomerDetails({ customerQuery }: CustomerTileProps) {
	return (
		<>
			<Grid w={'100%'}>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Card number'}
						isUndefined={!customerQuery.data?.value.phone}
					>
						<InfoBadge
							value={customerQuery.data?.value.cardNo}
							color="medepos.6"
						/>
					</Detail>
				</Grid.Col>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Discount'}
						isUndefined={!customerQuery.data?.value.discount}
					>
						<InfoBadge
							value={formatToCurrencyOrDefault(
								customerQuery.data?.value.discount,
							)}
							color="medepos.6"
						/>
					</Detail>
				</Grid.Col>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Purchases'}
						isUndefined={!customerQuery.data?.value.totalPurchases}
					>
						<InfoBadge
							value={customerQuery.data?.value.totalPurchases.toString()}
							color="medepos.6"
						/>
					</Detail>
				</Grid.Col>
			</Grid>
		</>
	);
}

export function CustomerBankInformation({ customerQuery }: CustomerTileProps) {
	return (
		<>
			<Grid w={'100%'}>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Account Name'}
						stringValue={customerQuery.data?.value.bankAccountName}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Sort Code'}
						stringValue={customerQuery.data?.value.bankSortCode}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Account Number'}
						stringValue={customerQuery.data?.value.bankAccountNumber}
					/>
				</Grid.Col>
			</Grid>
		</>
	);
}

export function CustomerAddress({ customerQuery }: CustomerTileProps) {
	return (
		<>
			<Grid w={'100%'}>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Address Line One'}
						stringValue={customerQuery.data?.value.addressLineOne}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Address Line Two'}
						stringValue={customerQuery.data?.value.addressLineTwo}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Post Code'}
						stringValue={customerQuery.data?.value.postCode}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'County'}
						stringValue={customerQuery.data?.value.county}
					/>
				</Grid.Col>
				<Grid.Col span={4}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Town'}
						stringValue={customerQuery.data?.value.town}
					/>
				</Grid.Col>
			</Grid>
		</>
	);
}

export function CustomerGeneralInformation({
	customerQuery,
}: CustomerTileProps) {
	return (
		<>
			<Grid w={'100%'}>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'First Name'}
						stringValue={customerQuery.data?.value.firstName}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Last Name'}
						stringValue={customerQuery.data?.value.lastName}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Branch'}
						stringValue={customerQuery.data?.value.branch}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Birth Date'}
						stringValue={
							customerQuery.data?.value.birthDate
								? formatToDateString(customerQuery.data?.value.birthDate)
								: ''
						}
					/>
				</Grid.Col>
				<Divider w={'100%'} />
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Join Date'}
						stringValue={
							customerQuery.data?.value.joinDate
								? formatToDateString(customerQuery.data?.value.joinDate)
								: ''
						}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'End Date'}
						stringValue={
							customerQuery.data?.value.endDate
								? formatToDateString(customerQuery.data?.value.endDate)
								: ''
						}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Phone'}
						isUndefined={!customerQuery.data?.value.phone}
					>
						<InfoBadge value={customerQuery.data?.value.phone} />
					</Detail>
				</Grid.Col>
				<Grid.Col span={6}>
					<Detail
						isLoading={customerQuery.isLoading}
						label={'Email'}
						isUndefined={!customerQuery.data?.value.email}
					>
						<InfoBadge value={customerQuery.data?.value.email} />
					</Detail>
				</Grid.Col>
			</Grid>
		</>
	);
}
